import React, { useRef, useState } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { Copy, Check, Download } from 'lucide-react';
import Button from './Button';
import { TEXT } from '../../uiTokens';
import { canCopyImages, copyImageToClipboard, downloadBlob } from '../../utils/clipboard';

const DISPLAY_SIZE = 160;
const EXPORT_SIZE = 1024;

function toPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    if (!canvas?.toBlob) {
      reject(new Error('Canvas export is not supported'));
      return;
    }
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Could not render the QR image'));
    }, 'image/png');
  });
}

/**
 * Scannable QR for a share link, with clipboard and download export.
 *
 * The visible code is an SVG; a hidden high-resolution canvas backs the export
 * so a pasted image stays sharp. Everything is generated in the browser - the
 * link never round-trips to a server.
 */
export default function QrCard({ value, filename = 'hushify-link.png', onError }) {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const copyable = canCopyImages();

  const withBlob = async (action, failureMessage) => {
    try {
      const blob = await toPngBlob(canvasRef.current);
      await action(blob);
    } catch {
      if (onError) onError(failureMessage);
    }
  };

  const handleCopyImage = () => withBlob(
    async (blob) => {
      await copyImageToClipboard(blob);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    },
    'Could not copy the QR image. Download it instead.',
  );

  const handleDownload = () => withBlob(
    (blob) => downloadBlob(blob, filename),
    'Could not save the QR image.',
  );

  return (
    <div className="flex flex-col items-center gap-3 animate-rise-fast">
      <div className="rounded-lg bg-white p-3 shadow-e1">
        <QRCodeSVG
          value={value}
          size={DISPLAY_SIZE}
          level="H"
          marginSize={4}
          bgColor="#ffffff"
          fgColor="#000000"
          title="QR code for your secret link"
          className="h-auto w-full max-w-[160px]"
        />
      </div>

      <div className="hidden" aria-hidden="true">
        <QRCodeCanvas
          ref={canvasRef}
          value={value}
          size={EXPORT_SIZE}
          level="H"
          marginSize={4}
          bgColor="#ffffff"
          fgColor="#000000"
        />
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        {copyable && (
          <Button size="sm" variant={copied ? 'secondary' : 'primary'} onClick={handleCopyImage}>
            {copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
            {copied ? 'Copied' : 'Copy image'}
          </Button>
        )}
        <Button size="sm" variant={copyable ? 'ghost' : 'primary'} onClick={handleDownload}>
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
          Download .png
        </Button>
      </div>

      <p className={`${TEXT.caption} text-center max-w-[36ch]`}>
        The image contains the link. Treat it like the link itself.
      </p>
    </div>
  );
}
